import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Checkbox,
  FormGroup,
  FormControlLabel,
  CircularProgress,
  Box,
} from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { FullProperty } from "../models/fullProperty";
import { staffMapping } from "../models/staffMapping";
import { ProfileType } from "../models/profileType";
import { createStaffAccount } from "../services/functions/userService";

interface CreateStaffDialogProps {
  open: boolean;
  onClose: () => void;
  restaurant: FullProperty;
  sentBy: ProfileType;
  onSuccess: () => void;
}

interface FormValues {
  username: string;
  password: string;
}

const schema = yup.object({
  username: yup
    .string()
    .required("Username is required")
    .matches(
      /^[a-zA-Z0-9]+$/,
      "Only letters and numbers, no spaces or special characters"
    ),
  password: yup
    .string()
    .required("Password is required")
    .min(6, "Password must be at least 6 characters"),
});

export const CreateStaffDialog: React.FC<CreateStaffDialogProps> = ({
  open,
  onClose,
  restaurant,
  sentBy,
  onSuccess
}) => {
  const [error, setError] = useState<string>("");
  const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      username: "",
      password: "",
    },
  });

  const closeDialog = () => {
    reset();
    setError("");
    setPasswordVisible(false);
    onClose();
  };

  const onSubmit = async (data: FormValues) => {
    setLoading(true);
    setError("");

    try {
      // Build email alias
      const [localPart, domainPart] = sentBy.email.split("@");
      const emailAlias = `${localPart}+${data.username}@${domainPart}`;

      // 3) Write to your “staffMapping” collection
      const staffAlias: staffMapping = {
        createdBy: sentBy.email,
        createdByUid: sentBy.id,
        emailAlias,
        timeCreated: new Date().toISOString(),
        username: data.username,
        restaurantId: restaurant.id,
        userId: ""
      };


      await createStaffAccount({
        password: data.password,
        staffMapping: staffAlias
      })

      // 6) Close dialog, etc.
      onSuccess();
      closeDialog();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={closeDialog}>
      <DialogTitle>Create Staff Member</DialogTitle>
      {loading ?
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
        :
        <DialogContent>
          {error && (
            <Typography variant="body2" color="error" gutterBottom>
              {error}
            </Typography>
          )}

          {/* Username Field */}
          <Controller
            name="username"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                autoFocus
                margin="dense"
                label="Username"
                type="text"
                fullWidth
                variant="outlined"
                error={!!errors.username}
                helperText={errors.username?.message}
              />
            )}
          />

          {/* Password Field */}
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                margin="dense"
                label="Password"
                type={passwordVisible ? "text" : "password"}
                fullWidth
                variant="outlined"
                error={!!errors.password}
                helperText={errors.password?.message}
              />
            )}
          />

          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={passwordVisible}
                  onChange={(event) => {
                    setPasswordVisible(event.target.checked);
                  }}
                  sx={{
                    color: "#757575",
                    "&.Mui-checked": {
                      color: "#162C1F",
                    },
                    "&.MuiCheckbox-root:hover": {
                      backgroundColor: "#E6F0E8",
                    },
                  }}
                />
              }
              label="Show Password"
            />
          </FormGroup>
        </DialogContent>
      }

      <DialogActions>
        <Button onClick={closeDialog} color="primary" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          color="primary"
          disabled={loading}
        >
          {loading ? "Creating…" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
